import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, KeyRound, Cloud, Server, CheckCircle2, XCircle, Loader2, Save, Zap, ShieldCheck, Plus, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';

const PROVIDERS = [
  { key: 'emergent', label: 'Emergent Universal Key', icon: Zap, hint: 'Built-in key (OpenAI/Anthropic/Gemini). No setup — great default.', fields: [] },
  { key: 'openai', label: 'OpenAI', icon: KeyRound, fields: [{ k: 'api_key', label: 'API Key', secret: true, ph: 'sk-...' }] },
  { key: 'anthropic', label: 'Anthropic', icon: KeyRound, fields: [{ k: 'api_key', label: 'API Key', secret: true, ph: 'sk-ant-...' }] },
  { key: 'gemini', label: 'Google Gemini', icon: KeyRound, fields: [{ k: 'api_key', label: 'API Key', secret: true, ph: 'AIza...' }] },
  { key: 'azure', label: 'Azure OpenAI', icon: Cloud, fields: [
    { k: 'api_key', label: 'API Key', secret: true },
    { k: 'endpoint', label: 'Endpoint', ph: 'https://my-resource.openai.azure.com' },
    { k: 'api_version', label: 'API Version', ph: '2024-10-21' },
    { k: 'deployment', label: 'Default Deployment Name', ph: 'gpt-4o' },
  ] },
  { key: 'bedrock', label: 'AWS Bedrock', icon: Server, fields: [
    { k: 'auth_mode', label: 'Auth Mode', select: ['irsa', 'static'] },
    { k: 'region', label: 'AWS Region', ph: 'us-east-1' },
    { k: 'access_key_id', label: 'Access Key ID (static only)', ph: 'AKIA...' },
    { k: 'secret_access_key', label: 'Secret Access Key (static only)', secret: true },
  ] },
];

export default function LLMSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [providerForms, setProviderForms] = useState({});
  const [routing, setRouting] = useState({});
  const [defaultRoute, setDefaultRoute] = useState({ provider: 'emergent', model: 'gpt-4o' });
  const [testing, setTesting] = useState({});
  const [savingP, setSavingP] = useState({});
  const [savingRoute, setSavingRoute] = useState(false);
  const [privacy, setPrivacy] = useState({ enabled: true, mode: 'balanced', custom_patterns: [], min_cell_population: 10 });
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [activity, setActivity] = useState(null);

  const load = async () => {
    try {
      const s = await api.getLlmSettings();
      setSettings(s);
      setRouting(s.routing || {});
      setDefaultRoute(s.default || { provider: 'emergent', model: 'gpt-4o' });
      setPrivacy({ enabled: true, mode: 'balanced', custom_patterns: [], min_cell_population: 10, ...(s.privacy || {}) });
      const forms = {};
      PROVIDERS.forEach(p => { forms[p.key] = { ...(s.providers?.[p.key] || {}) }; });
      setProviderForms(forms);
    } catch (e) { toast.error(e.message); }
    try { setActivity(await api.getPrivacyActivity(7)); } catch { /* non-fatal */ }
  };
  useEffect(() => { load(); }, []);

  const modelsFor = (provider) => {
    if (!settings) return [];
    if (provider === 'azure') {
      const dep = providerForms.azure?.deployment;
      return dep ? [dep] : [];
    }
    return settings.provider_models?.[provider] || [];
  };

  const saveProvider = async (key) => {
    setSavingP(s => ({ ...s, [key]: true }));
    try {
      await api.updateLlmProvider(key, providerForms[key] || {});
      toast.success(`${key} settings saved`);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setSavingP(s => ({ ...s, [key]: false })); }
  };

  const testProvider = async (key) => {
    const model = (modelsFor(key)[0]) || providerForms[key]?.deployment || 'gpt-4o';
    setTesting(s => ({ ...s, [key]: { loading: true } }));
    try {
      const r = await api.testLlmConnection({ provider: key, model, config: providerForms[key] });
      setTesting(s => ({ ...s, [key]: r }));
      r.ok ? toast.success(`${key}: connection OK`) : toast.error(`${key}: ${r.error || 'failed'}`);
    } catch (e) {
      setTesting(s => ({ ...s, [key]: { ok: false, error: e.message } }));
      toast.error(e.message);
    }
  };

  const saveRouting = async () => {
    setSavingRoute(true);
    try {
      await api.updateLlmRouting({ routing, default: defaultRoute });
      toast.success('Feature routing saved');
    } catch (e) { toast.error(e.message); }
    finally { setSavingRoute(false); }
  };

  const savePrivacy = async () => {
    setSavingPrivacy(true);
    try {
      const clean = (privacy.custom_patterns || []).filter(r => (r.pattern || '').trim());
      const res = await api.updateLlmPrivacy({ ...privacy, custom_patterns: clean });
      setPrivacy({ enabled: true, mode: 'balanced', custom_patterns: [], min_cell_population: 10, ...(res.privacy || {}) });
      toast.success('Privacy gateway settings saved');
    } catch (e) { toast.error(e.message); }
    finally { setSavingPrivacy(false); }
  };

  const setField = (pkey, fkey, val) => setProviderForms(f => ({ ...f, [pkey]: { ...(f[pkey] || {}), [fkey]: val } }));
  const setFeatureRoute = (feat, patch) => setRouting(r => ({ ...r, [feat]: { ...(r[feat] || defaultRoute), ...patch } }));

  if (!settings) return <div className="p-8 text-muted-foreground" data-testid="llm-settings-loading">Loading AI settings…</div>;

  const routableProviders = PROVIDERS.map(p => p.key);

  const RouteRow = ({ featKey, label, description }) => {
    const r = routing[featKey] || {};
    const prov = r.provider || '';
    return (
      <div className="grid grid-cols-12 items-center gap-2 py-2 border-b border-border/30" data-testid={`route-row-${featKey}`}>
        <div className="col-span-5 text-sm text-foreground">{label}
          {description && <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">{description}</div>}
          <div className="text-[10px] text-muted-foreground/80 font-mono">{featKey}</div></div>
        <div className="col-span-3">
          <Select value={prov || '__default__'} onValueChange={v => setFeatureRoute(featKey, { provider: v === '__default__' ? undefined : v, model: undefined })}>
            <SelectTrigger className="h-8 text-xs" data-testid={`route-provider-${featKey}`}><SelectValue placeholder="Use default" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__" className="text-xs">Use default</SelectItem>
              {routableProviders.map(p => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-4">
          {prov && (modelsFor(prov).length > 0 ? (
            <Select value={r.model || ''} onValueChange={v => setFeatureRoute(featKey, { model: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`route-model-${featKey}`}><SelectValue placeholder="model" /></SelectTrigger>
              <SelectContent>{modelsFor(prov).map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input value={r.model || ''} onChange={e => setFeatureRoute(featKey, { model: e.target.value })} placeholder="model / deployment" className="h-8 text-xs" data-testid={`route-model-input-${featKey}`} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="llm-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center"><Sparkles className="w-5 h-5 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI / LLM Providers</h1>
          <p className="text-sm text-muted-foreground">Bring your own model. Configure providers and route each AI feature to the model of your choice.</p>
        </div>
      </div>

      {/* Trusted-AI Privacy Gateway */}
      <Card className="p-5 space-y-4 border-emerald-500/30" data-testid="privacy-gateway-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0"><ShieldCheck className="w-5 h-5 text-emerald-400" /></div>
            <div>
              <h2 className="text-base font-semibold">Privacy Gateway</h2>
              <p className="text-sm text-muted-foreground max-w-2xl">Tokenizes customer specifics — policy/claim numbers, names, addresses, emails, phones, SSNs and your own house identifiers — <strong>before</strong> any question reaches the AI, then restores them in the answer. Governed labels (line of business, state, product) pass through so interpretation quality is unchanged. The token map never leaves this server.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Switch checked={!!privacy.enabled} onCheckedChange={v => setPrivacy(p => ({ ...p, enabled: v }))} data-testid="privacy-enabled-switch" />
            <span className="text-xs text-muted-foreground">{privacy.enabled ? 'On' : 'Off'}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Redaction mode</Label>
            <Select value={privacy.mode} onValueChange={v => setPrivacy(p => ({ ...p, mode: v }))}>
              <SelectTrigger className="w-56 h-9" data-testid="privacy-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off — send text as-is</SelectItem>
                <SelectItem value="balanced">Balanced (recommended)</SelectItem>
                <SelectItem value="strict">Strict — aggressive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground max-w-md">
            {privacy.mode === 'off' && 'No redaction. The full prompt is sent to the provider.'}
            {privacy.mode === 'balanced' && 'Structured identifiers plus keyword-anchored names & addresses are tokenized; governed labels pass.'}
            {privacy.mode === 'strict' && 'Balanced plus long digit-runs and standalone proper-name detection (higher recall).'}
          </p>
          <div className="space-y-1">
            <Label className="text-xs">Minimum cell population</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min={1} value={privacy.min_cell_population ?? 10}
                onChange={e => setPrivacy(p => ({ ...p, min_cell_population: parseInt(e.target.value || '10', 10) }))}
                className="w-24 h-9" data-testid="privacy-min-cell-input" />
              <span className="text-[11px] text-muted-foreground max-w-xs">Cells backed by fewer underlying records are hidden; thin whole-answer figures are flagged low-reliability.</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">House identifier patterns (optional)</Label>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setPrivacy(p => ({ ...p, custom_patterns: [...(p.custom_patterns || []), { pattern: '', category: 'HOUSE' }] }))} data-testid="privacy-add-pattern-btn"><Plus className="w-3 h-3 mr-1" />Add pattern</Button>
          </div>
          {(privacy.custom_patterns || []).length === 0 && (
            <p className="text-[11px] text-muted-foreground/70 italic">Add a regex for any internal ID format you use, e.g. <code>CUST-\d&#123;4&#125;</code>.</p>
          )}
          {(privacy.custom_patterns || []).map((row, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`privacy-pattern-row-${i}`}>
              <Input value={row.pattern} onChange={e => setPrivacy(p => { const c = [...p.custom_patterns]; c[i] = { ...c[i], pattern: e.target.value }; return { ...p, custom_patterns: c }; })} placeholder="Regex e.g. CUST-\d{4}" className="h-8 text-xs font-mono flex-1" data-testid={`privacy-pattern-input-${i}`} />
              <Input value={row.category} onChange={e => setPrivacy(p => { const c = [...p.custom_patterns]; c[i] = { ...c[i], category: e.target.value }; return { ...p, custom_patterns: c }; })} placeholder="Label" className="h-8 text-xs w-32" data-testid={`privacy-pattern-cat-${i}`} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setPrivacy(p => ({ ...p, custom_patterns: p.custom_patterns.filter((_, j) => j !== i) }))} data-testid={`privacy-pattern-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <Button size="sm" onClick={savePrivacy} disabled={savingPrivacy} data-testid="save-privacy-btn">
            {savingPrivacy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save privacy settings
          </Button>
        </div>

        {activity && (
          <div className="pt-3 border-t border-border/40" data-testid="privacy-activity">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Redaction activity — last {activity.days} days</span>
              <span className="text-[11px] text-muted-foreground">
                <strong className="text-foreground" data-testid="privacy-activity-total">{(activity.total_redacted || 0).toLocaleString()}</strong> identifiers redacted across{' '}
                <strong className="text-foreground">{(activity.calls_with_redaction || 0).toLocaleString()}</strong>/{(activity.total_calls || 0).toLocaleString()} AI calls
              </span>
            </div>
            {Object.keys(activity.by_category || {}).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(activity.by_category).sort((a, b) => b[1] - a[1]).map(([cat, n]) => (
                  <span key={cat} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/10 border border-emerald-500/20 text-emerald-300" data-testid={`privacy-cat-${cat}`}>
                    {cat} <strong>{n.toLocaleString()}</strong>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground/70 italic">No customer identifiers have needed redaction yet — questions so far contained only governed labels.</p>
            )}
          </div>
        )}
      </Card>

      {/* Providers */}
      <div className="grid md:grid-cols-2 gap-4">
        {PROVIDERS.map(p => {
          const Icon = p.icon; const t = testing[p.key]; const cfg = providerForms[p.key] || {};
          return (
            <Card key={p.key} className="p-4 space-y-3" data-testid={`provider-card-${p.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-primary" /><span className="font-medium text-sm">{p.label}</span></div>
                {p.key !== 'emergent' && (
                  <Switch checked={!!cfg.enabled} onCheckedChange={v => setField(p.key, 'enabled', v)} data-testid={`provider-enabled-${p.key}`} />
                )}
              </div>
              {p.hint && <p className="text-[11px] text-muted-foreground">{p.hint}</p>}
              {p.fields.map(f => (
                <div key={f.k}>
                  <Label className="text-[10px] text-muted-foreground">{f.label}</Label>
                  {f.select ? (
                    <Select value={cfg[f.k] || f.select[0]} onValueChange={v => setField(p.key, f.k, v)}>
                      <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`field-${p.key}-${f.k}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{f.select.map(o => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.secret ? 'password' : 'text'}
                      value={cfg[f.k] || ''}
                      onChange={e => setField(p.key, f.k, e.target.value)}
                      placeholder={f.ph || (f.secret ? '••••••••' : '')}
                      className="h-8 text-xs mt-0.5 font-mono"
                      data-testid={`field-${p.key}-${f.k}`}
                    />
                  )}
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                {p.fields.length > 0 && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => saveProvider(p.key)} disabled={savingP[p.key]} data-testid={`save-provider-${p.key}`}>
                    {savingP[p.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => testProvider(p.key)} disabled={t?.loading} data-testid={`test-provider-${p.key}`}>
                  {t?.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test connection'}
                </Button>
                {t && !t.loading && (t.ok
                  ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px] gap-1"><CheckCircle2 className="w-3 h-3" /> OK</Badge>
                  : <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-[10px] gap-1" title={t.error}><XCircle className="w-3 h-3" /> Failed</Badge>)}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Per-feature routing */}
      <Card className="p-5" data-testid="routing-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold">Per-feature model routing</h2>
            <p className="text-xs text-muted-foreground">Pick which provider + model powers each AI feature. Leave on "Use default" to inherit the default below.</p>
          </div>
          <Button size="sm" onClick={saveRouting} disabled={savingRoute} data-testid="save-routing-btn">
            {savingRoute ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save routing
          </Button>
        </div>

        <div className="grid grid-cols-12 items-center gap-2 py-2 bg-muted/40 rounded-lg px-2 mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          <div className="col-span-5">Default (fallback)</div>
          <div className="col-span-3">
            <Select value={defaultRoute.provider} onValueChange={v => setDefaultRoute({ provider: v, model: (settings.provider_models?.[v] || ['gpt-4o'])[0] })}>
              <SelectTrigger className="h-8 text-xs" data-testid="default-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{routableProviders.map(p => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-4">
            {modelsFor(defaultRoute.provider).length > 0 ? (
              <Select value={defaultRoute.model} onValueChange={v => setDefaultRoute({ ...defaultRoute, model: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="default-model"><SelectValue /></SelectTrigger>
                <SelectContent>{modelsFor(defaultRoute.provider).map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input value={defaultRoute.model} onChange={e => setDefaultRoute({ ...defaultRoute, model: e.target.value })} className="h-8 text-xs" data-testid="default-model-input" />
            )}
          </div>
        </div>

        {(() => {
          const byKey = Object.fromEntries((settings.features || []).map(f => [f.key, f]));
          const groups = settings.feature_groups && settings.feature_groups.length
            ? settings.feature_groups
            : [{ category: '', keys: (settings.features || []).map(f => f.key) }];
          return groups.map(g => {
            const keys = (g.keys || []).filter(k => byKey[k]);
            if (!keys.length) return null;
            return (
              <div key={g.category || 'all'} className="mt-4" data-testid={`route-group-${(g.category || 'all').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {g.category && (
                  <div className="text-xs font-semibold text-primary/90 uppercase tracking-wide mt-2 mb-1 pt-2 border-t border-border/40">{g.category}</div>
                )}
                {keys.map(k => <RouteRow key={k} featKey={k} label={byKey[k].label} description={byKey[k].description} />)}
              </div>
            );
          });
        })()}
      </Card>
    </div>
  );
}
